import { useCallback } from "react";
import { useApi } from "../../../../hooks/callApi/callApi";
import {
  CalendarResponse,
  CurrentWeekResponse,
} from "../../types/calendar";
import {
  Classifications,
  Conferences,
  GamesResponse,
  Teams,
} from "../../types/cfbd";
import { PollsResponse } from "../../types/poll";
import { RecordsResponse } from "../../types/records";

/**
 * Custom hook for CFBD operations
 */
export const useCfbd = () => {
  const callApi = useApi();

  const getCurrentWeek = useCallback(async (): Promise<
    CurrentWeekResponse | Error
  > => {
    try {
      const response = await callApi(
        `cfbd/calendar/current`,
        {
          method: "GET",
        },
        ""
      );
      if (!response.ok) throw new Error("Failed to fetch current week");
      const currentWeek: CurrentWeekResponse = await response.json();
      return currentWeek;
    } catch (err: any) {
      return err instanceof Error ? err : new Error("Unknown error");
    }
  }, [callApi]);

  const getCalendarForYear = useCallback(
    async (year: number): Promise<CalendarResponse | Error> => {
      try {
        const response = await callApi(
          `cfbd/calendar/${year}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch calendar for year");
        const calendar: CalendarResponse = await response.json();
        return calendar;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getAvailableWeeks = useCallback(
    async (year: number): Promise<number[] | Error> => {
      try {
        const calendarResponse = await getCalendarForYear(year);
        if (calendarResponse instanceof Error) {
          return calendarResponse;
        }

        const weeks = Array.isArray(calendarResponse?.weeks)
          ? calendarResponse.weeks
              .map((w) => Number(w?.week))
              .filter((n) => Number.isFinite(n))
              .sort((a, b) => a - b)
          : [];

        return weeks;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [getCalendarForYear]
  );

  const getClassifications = useCallback(async (): Promise<
    Classifications | Error
  > => {
    try {
      const response = await callApi(
        `cfbd/classifications`,
        {
          method: "GET",
        },
        ""
      );
      if (!response.ok) throw new Error("Failed to fetch classifications");
      const classifications: Classifications = await response.json();
      return classifications;
    } catch (err: any) {
      return err instanceof Error ? err : new Error("Unknown error");
    }
  }, [callApi]);

  const getConferences = useCallback(
    async (classification: string): Promise<Conferences | Error> => {
      try {
        const response = await callApi(
          `cfbd/conferences?classification=${classification}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch conferences");
        const conferences: Conferences = await response.json();
        return conferences;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getGames = useCallback(
    async (
      classification: string,
      conference: string,
      week: number,
      year: number
    ): Promise<GamesResponse | Error> => {
      try {
        const response = await callApi(
          `cfbd/games?classification=${classification}&conference=${conference}&week=${week}&year=${year}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch games");
        const responseData: GamesResponse = await response.json();
        return responseData;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getTeams = useCallback(
    async (teamIds: number[]): Promise<Teams | Error> => {
      try {
        const response = await callApi(
          `cfbd/teams?ids=${teamIds.join(",")}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch teams");
        const teams: Teams = await response.json();
        return teams;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getPolls = useCallback(
    async (year: number, week: number): Promise<PollsResponse | Error> => {
      try {
        const response = await callApi(
          `cfbd/polls?year=${year}&week=${week}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch polls");
        const polls: PollsResponse = await response.json();
        return polls;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getRecords = useCallback(
    async (
      year: number,
      teamIds: number[]
    ): Promise<RecordsResponse | Error> => {
      try {
        const response = await callApi(
          `cfbd/records?ids=${teamIds.join(",")}&year=${year}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch records");
        const records: RecordsResponse = await response.json();
        return records;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getPollsAndRecords = useCallback(
    async (
      year: number,
      week: number,
      teamIds: number[]
    ): Promise<{ polls: PollsResponse; records: RecordsResponse } | Error> => {
      try {
        const recordsResponse = await getRecords(year, teamIds);
        if (recordsResponse instanceof Error) {
          return recordsResponse;
        }
        const pollsResponse = await getPolls(year, week);
        if (pollsResponse instanceof Error) {
          return pollsResponse;
        }
        return { polls: pollsResponse, records: recordsResponse };
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [getPolls, getRecords]
  );

  return {
    getCurrentWeek,
    getCalendarForYear,
    getAvailableWeeks,
    getClassifications,
    getConferences,
    getGames,
    getTeams,
    getPolls,
    getRecords,
    getPollsAndRecords,
  };
};

export default useCfbd;
